import React, {useState, useEffect} from "react";
import {useLocation, useNavigate, Link} from "react-router-dom";
import {toast} from "react-toastify";
import {confirmRegister, retryOtpRegister} from "../../../services/auth";
import {useTranslation} from "react-i18next";
import {trackEventThen} from "../../../utils/analytics";
import styles from "./RegisterConfirmPage.module.css";

const RegisterConfirmPage = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [otp, setOtp] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const [error, setError] = useState("");

    // location.state is wiped on page reload, so fall back to sessionStorage
    // (set in RegisterPage on successful submit) — keeps resend OTP working
    // after a refresh / wait.
    const verificationToken =
        location.state?.verificationToken ||
        sessionStorage.getItem("gredus.registerToken");

    useEffect(() => {
        if (!verificationToken) {
            toast.error(t("auth.otpVerificationTokenMissing"));
            navigate("/register", {replace: true});
        }
    }, [verificationToken, navigate, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (otp.length !== 6) {
            setError(t("auth.otpMustBe6Digits"));
            return;
        }

        try {
            setIsSubmitting(true);
            setError("");
            const data = await confirmRegister(verificationToken, parseInt(otp));
            // Registration confirmed — the token is consumed server-side; clear it.
            sessionStorage.removeItem("gredus.registerToken");

            toast.success(t("toasts.registerCompleted"));
            // The account exists only once the OTP clears, so this is the real conversion — mark it
            // in GA4 before leaving for the tenant subdomain, where analytics deliberately does not
            // run. trackEventThen guarantees the redirect happens even if gtag is blocked.
            trackEventThen("sign_up", {method: "email"}, () => {
                window.location.replace(`https://${data.data.tenantSlug}.gredus.com/login`);
            });
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err.message ||
                t("auth.otpVerifyError");
            setError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResendOtp = async (e) => {
        e.preventDefault();
        try {
            setIsRetrying(true);
            setError("");
            await retryOtpRegister(verificationToken);
            toast.success(t("toasts.newOtpSent"));
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err.message ||
                t("auth.otpResendError");
            setError(msg);
        } finally {
            setIsRetrying(false);
        }
    }

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.formContainer}>
                    <Link to="/">
                        <img src="/gredus-logo.svg" alt="logo" className={styles.logo}/>
                    </Link>
                    <h2 className={styles.title}>{t("auth.otpTitle")}</h2>
                    <p className={styles.description}>
                        {t("auth.otpRegisterDescription")}
                    </p>

                    <form onSubmit={handleSubmit} className={styles.form}>
                        {error && <p className={styles.error}>{error}</p>}

                        <div className={styles.formGroup}>
                            <input
                                type="text"
                                maxLength="6"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                className={styles.input}
                                placeholder="123456"
                                autoFocus
                            />
                        </div>

                        <button type="submit" disabled={isSubmitting || otp.length !== 6} className={styles.submitBtn}>
                            {isSubmitting ? t("auth.otpVerifying") : t("auth.otpConfirm")}
                        </button>
                    </form>

                    <p className={styles.backText}>
                        {t("auth.otpResendPrompt")}{" "}
                        <span onClick={handleResendOtp}
                              className={`${styles.resendLink} ${isRetrying ? styles.disabled : ""}`}>
                            {isRetrying ? t("auth.otpResendSending") : t("auth.otpResendLink")}
                        </span>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default RegisterConfirmPage;
